"use client"

import { useState, useRef, useEffect } from "react"
import { XMark } from "@medusajs/icons"
import { Button, Text } from "@medusajs/ui"
import { HttpTypes } from "@medusajs/types"
import { SearchFilters } from "../../templates/search-template"
import { getCategoryNameById, getHierarchicalCategories } from "@/lib/util/category-helpers"
import { FILTER_TITLE_MAP } from "../search-filters"

type ActiveFiltersProps = {
  filters: SearchFilters
  onRemoveFilter: (filters: Partial<SearchFilters>) => void
  onClearAll: () => void
  categories: HttpTypes.StoreProductCategory[]
}

export default function ActiveFilters({
  filters,
  onRemoveFilter,
  onClearAll,
  categories,
}: ActiveFiltersProps) {
  // Add price formatting function
  const formatPrice = (price: number) => {
    return `€${Math.round(price).toLocaleString()}`
  }

  const [draggedChip, setDraggedChip] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const hasActiveFilters = Object.values(filters).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0
    }
    if (typeof value === "boolean") {
      return value
    }
    if (typeof value === "object" && value !== null) {
      const priceRange = value as [number, number]
      return priceRange[0] > 0 || priceRange[1] < 10000
    }
    return false
  })

  if (!hasActiveFilters) {
    return null
  }

  // Handle haptic feedback
  const triggerHapticFeedback = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(30)
    }
  }

  // Generic function to remove filter value
  const removeFilterValue = (
    filterType: keyof Omit<SearchFilters, "priceRange" | "inStock" | "fastDelivery" | "bulkPricing" | "programmable">,
    value: string
  ) => {
    triggerHapticFeedback()
    onRemoveFilter({
      [filterType]: (filters[filterType] as string[]).filter(item => item !== value)
    })
  }

  const removePriceRange = () => {
    triggerHapticFeedback()
    onRemoveFilter({
      priceRange: [0, 10000]
    })
  }

  const removeQuickFilter = (filterType: keyof Pick<SearchFilters, "inStock" | "fastDelivery" | "bulkPricing" | "programmable">) => {
    triggerHapticFeedback()
    onRemoveFilter({
      [filterType]: false
    })
  }

  const handleClearAll = () => {
    triggerHapticFeedback()
    onClearAll()
  }

  // Function to get categories for display with correct subcategory logic
  const getDisplayCategories = () => {
    const { mainCategories, subcategoriesByParent } = getHierarchicalCategories(categories)
    
    // Get all selected category IDs
    const selectedCategoryIds = filters.categories
    
    // Separate main categories and subcategories
    const selectedMainCategories = selectedCategoryIds.filter(id => 
      mainCategories.some(mainCat => mainCat.id === id)
    )
    
    // Get all subcategories from the subcategoriesByParent object
    const allSubCategories = Object.values(subcategoriesByParent).flat()
    
    const selectedSubCategories = selectedCategoryIds.filter(id => 
      allSubCategories.some((subCat: HttpTypes.StoreProductCategory) => subCat.id === id)
    )
    
    // If main categories are selected, don't show their subcategories
    // But still show directly selected subcategories
    const displayCategories = [...selectedMainCategories]
    
    // Add subcategories that are not children of selected main categories
    selectedSubCategories.forEach(subCatId => {
      const subCategory = allSubCategories.find((sub: HttpTypes.StoreProductCategory) => sub.id === subCatId)
      if (subCategory && subCategory.parent_category_id) {
        // Check if this subcategory's parent is not in the selected main categories
        const parentIsSelected = selectedMainCategories.includes(subCategory.parent_category_id)
        if (!parentIsSelected) {
          displayCategories.push(subCatId)
        }
      }
    })
    
    return displayCategories
  }

  const FilterChip = ({ 
    label, 
    onRemove, 
    variant = "default",
    chipId
  }: { 
    label: string
    onRemove: () => void
    variant?: "default" | "price" | "quick" | "brand" | "material" | "category"
    chipId: string
  }) => {
    const [startX, setStartX] = useState(0)
    const [currentX, setCurrentX] = useState(0)
    const [isDragging, setIsDragging] = useState(false)

    const getChipStyles = () => {
      const baseStyles = "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer select-none"
      
      switch (variant) {
        case "price":
          return `${baseStyles} bg-yellow-100 text-yellow-800 border border-yellow-200 hover:bg-yellow-200`
        case "quick":
          return `${baseStyles} bg-green-100 text-green-800 border border-green-200 hover:bg-green-200`
        case "brand":
          return `${baseStyles} bg-pink-100 text-pink-800 border border-pink-200 hover:bg-pink-200`
        case "material":
          return `${baseStyles} bg-orange-100 text-orange-800 border border-orange-200 hover:bg-orange-200`
        case "category":
          return `${baseStyles} bg-indigo-100 text-indigo-800 border border-indigo-200 hover:bg-indigo-200`
        default:
          return `${baseStyles} bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200`
      }
    }

    const handleTouchStart = (e: React.TouchEvent) => {
      setStartX(e.touches[0].clientX)
      setIsDragging(false)
    }

    const handleTouchMove = (e: React.TouchEvent) => {
      const currentX = e.touches[0].clientX
      const diff = Math.abs(currentX - startX)
      
      if (diff > 10) {
        setIsDragging(true)
        setCurrentX(currentX)
      }
    }

    const handleTouchEnd = () => {
      if (isDragging && Math.abs(currentX - startX) > 50) {
        onRemove()
      }
      setIsDragging(false)
    }

    return (
      <div
        className={getChipStyles()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: isDragging ? `translateX(${currentX - startX}px)` : 'translateX(0)',
          opacity: isDragging ? 0.7 : 1,
        }}
      >
        <span className="truncate max-w-[120px]">{label}</span>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="ml-1 p-0.5 rounded-full hover:bg-black/10 transition-colors"
        >
          <XMark className="w-3 h-3" />
        </button>
      </div>
    )
  }

  const activeFilterCount = Object.keys(filters).filter(key => 
    ![
      "priceRange", 
      "inStock", 
      "fastDelivery", 
      "bulkPricing", 
      "programmable"
    ].includes(key) && Array.isArray(filters[key as keyof SearchFilters]) && (filters[key as keyof SearchFilters] as string[]).length > 0
  ).length

  // Get categories for display
  const displayCategories = getDisplayCategories()

  return (
    <div className="px-4 py-3 sticky top-[73px] z-10">
      <div
        ref={scrollRef}
        className="flex flex-wrap gap-2"
      >
        {/* Category Filters - Show main categories and directly selected subcategories */}
        {displayCategories.map((categoryId) => (
          <FilterChip
            key={`category-${categoryId}`}
            chipId={`category-${categoryId}`}
            label={getCategoryNameById(categoryId, categories) || categoryId}
            onRemove={() => removeFilterValue("categories", categoryId)}
            variant="category"
          />
        ))}

        {/* Brand Filters */}
        {filters.brands.map((brand) => (
          <FilterChip
            key={`brand-${brand}`}
            chipId={`brand-${brand}`}
            label={brand}
            onRemove={() => removeFilterValue("brands", brand)}
            variant="brand"
          />
        ))}


        {/* Subcategory Filters */}
        {filters.subcategories.map((subcategory) => (
          <FilterChip
            key={`subcategory-${subcategory}`}
            chipId={`subcategory-${subcategory}`}
            label={subcategory}
            onRemove={() => removeFilterValue("subcategories", subcategory)}
            variant="category"
          />
        ))}

        {Object.entries(filters).map(([key, value]) => {
          if (
            ![
              "categories",
              "brands",
              "subcategories",
              "priceRange",
              "inStock",
              "fastDelivery",
              "bulkPricing",
              "programmable",
            ].includes(key) &&
            Array.isArray(value) &&
            value.length > 0
          ) {
            return value.map((item) => {
              const filterTitle = FILTER_TITLE_MAP[key as keyof typeof FILTER_TITLE_MAP] || key
              return (
                <FilterChip
                  key={`${key}-${item}`}
                  chipId={`${key}-${item}`}
                  label={`${filterTitle}: ${item}`}
                  onRemove={() =>
                    removeFilterValue(key as any, item)
                  }
                  variant="material"
                />
              )
            })
          }
          return null
        })}
        {/* Main Material Filters */}

        {/* Price Range Filter */}
        {(filters.priceRange[0] > 0 || filters.priceRange[1] < 10000) && (
          <FilterChip
            chipId="price-range"
            label={`${formatPrice(
              filters.priceRange[0]
            )} - ${formatPrice(filters.priceRange[1])}`}
            onRemove={removePriceRange}
            variant="price"
          />
        )}

        {/* Quick Filters */}
        {filters.fastDelivery && (
          <FilterChip
            chipId="fast-delivery"
            label="Samo na zalogi"
            onRemove={() => removeQuickFilter("fastDelivery")}
            variant="quick"
          />
        )}
        {filters.bulkPricing && (
          <FilterChip
            chipId="bulk-pricing"
            label="Količinske cene"
            onRemove={() => removeQuickFilter("bulkPricing")}
            variant="quick"
          />
        )}
        {filters.programmable && (
          <FilterChip
            chipId="programmable"
            label="Programabilen"
            onRemove={() => removeQuickFilter("programmable")}
            variant="quick"
          />
        )}
      </div>
    </div>
  )
} 